/*
 * Username policy: decides whether a handle may be registered.
 *
 * The problem with a plain blocklist is that `admin` is one entry but
 * `Admin`, `4dm1n`, `aaadmin`, `_admin_`, `admin_official`, `xXadminXx`,
 * `аdmin` (Cyrillic а) and `adm1n` are all the same handle to a human reader.
 * So nothing is compared literally. Both sides - the candidate and every
 * blocklist term - are folded to a "skeleton" first, and terms are compiled
 * into patterns where a repeated letter may only expand, never contract
 * (`coon` matches `cooon` but not `con`).
 *
 * Checks run cheapest-first and stop at the first hit:
 *
 *   1. shape        length, charset, underscores, all-digits (publicId clash)
 *   2. exact        skeleton equals a reserved/role/test/brand/hate term
 *   3. stripped     same, after peeling filler affixes (the/real/official/xx/123)
 *   4. substring    slurs, hate and profanity anywhere in the skeleton
 *                   (short stems: only as a whole token, after masking
 *                    innocent words such as `pakistan` or `cockburn`)
 *   5. token        a role word as its own token: `atrium_support_team`
 *   6. fuzzy        edit distance 1-2 from a role or brand: `g00gle`, `amaz0n`,
 *                   `paypaI`, `micr0soft`, `atriumsupprt`
 *
 * Every rejection names a category so callers can hard-block some and queue
 * others for human review.
 */
import {
  AUTHORITY_TERMS,
  BRAND,
  COMPANY_NAMES,
  HATE_TERMS,
  INNOCENT_WORDS,
  PROFANITY_STEMS,
  QUALIFIER_WORDS,
  RESERVED_ROUTES,
  ROLE_HANDLE_TERMS,
  SLUR_STEMS,
  TEST_TERMS,
} from "./username-blocklist";

export const USERNAME_MIN = 2;
export const USERNAME_MAX = 24;
export const USERNAME_CHARSET = /^[a-zA-Z0-9_]+$/;

export type UsernameCategory =
  | "shape"
  | "reserved"
  | "authority"
  | "test"
  | "brand"
  | "slur"
  | "hate"
  | "profanity"
  | "impersonation";

/** `block` refuses the signup; `review` lets it through but should be queued for a human. */
export type UsernameAction = "allow" | "review" | "block";

export interface UsernameVerdict {
  ok: boolean;
  action: UsernameAction;
  /** Why it was refused; absent when `ok`. */
  category?: UsernameCategory;
  /** The blocklist term (or shape rule) that matched - for logs and moderation, not for users. */
  term?: string;
  /** Safe to show the user. */
  message?: string;
  /** Folded form used for matching; also the right key for a "no two confusable handles" unique index. */
  skeleton: string;
}

export interface UsernamePolicyOptions {
  /** Treat company names as `review` instead of `block` (lets a human judge `apple` vs `apple_support`). */
  brandAction?: Extract<UsernameAction, "review" | "block">;
  /** Extra slur stems, e.g. from a maintained dataset. Matched exactly like the built-in list. */
  extraSlurs?: readonly string[];
  /** Extra reserved words (new routes, campaign names, retired handles). */
  extraReserved?: readonly string[];
  /** Words that must stop being treated as suspicious - add real signups that were wrongly refused. */
  extraInnocent?: readonly string[];
  /** Skip profanity matching (keep slurs/hate). Off by default: handles are public and unavoidable. */
  allowProfanity?: boolean;
}

/* ---------------------------------------------------------------- folding -- */

/**
 * Homoglyphs NFKD does not fold: Cyrillic, Greek and Latin lookalikes. Mixed-script
 * handles are already impossible under USERNAME_CHARSET, but display names and a
 * future wider charset need this, and folding here costs nothing.
 */
const HOMOGLYPHS: Record<string, string> = {
  // Cyrillic
  "а": "a", "в": "b", "с": "c", "е": "e", "н": "h", "к": "k", "м": "m",
  "о": "o", "р": "p", "т": "t", "х": "x", "у": "y", "і": "i", "ј": "j",
  "ѕ": "s", "ԁ": "d", "ԛ": "q", "ѡ": "w", "и": "u", "г": "r", "л": "n",
  // Greek
  "α": "a", "β": "b", "ε": "e", "η": "n", "ι": "i", "κ": "k", "μ": "u",
  "ν": "v", "ο": "o", "ρ": "p", "σ": "o", "τ": "t", "υ": "u", "χ": "x",
  "ω": "w", "θ": "o",
  // Latin oddities
  "ı": "i", "ȷ": "j", "ł": "l", "ø": "o", "đ": "d", "ƀ": "b", "ɑ": "a",
  "ɛ": "e", "ſ": "f", "ɡ": "g", "ʏ": "y", "ɩ": "i",
};

/**
 * Characters that read as the same letter. `i`/`l`/`1` collapse into one class
 * (`paypaI`, `paypa1` and `paypal` are the same handle to the eye), and so do
 * the usual digit-for-letter swaps.
 */
const LEET_CLASSES: Record<string, string> = {
  "4": "a", "@": "a", "8": "b", "(": "c", "3": "e", "6": "g", "9": "g", "1": "i", "l": "i", "!": "i",
  "|": "i", "0": "o", "5": "s", "$": "s", "7": "t", "+": "t", "2": "z",
};

/** Lowercase, drop diacritics and decompose fullwidth/styled characters. */
function flatten(raw: string): string {
  return raw
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/** Canonical comparison form: letters and digits only, homoglyphs and leet folded. */
export function skeletonize(raw: string): string {
  let out = "";
  for (const ch of flatten(raw)) {
    const c = HOMOGLYPHS[ch] ?? ch;
    const folded = LEET_CLASSES[c] ?? c;
    if (/[a-z0-9]/.test(folded)) out += folded;
  }
  // `vv`/`rn` are typed to look like `w`/`m` (vvalmart, arnazon).
  return out.replace(/vv/g, "w").replace(/rn/g, "m");
}

/** Filler people wrap around a protected word to slip past an exact-match list. */
const FILLER_PREFIX = /^(?:the|real|official|offical|its|itz|im|iam|mr|mrs|ms|dr|sir|lord|king|queen|x+|_+|[0-9]+)/;
const FILLER_SUFFIX = /(?:official|offical|real|hq|team|inc|ltd|llc|corp|app|bot|tv|yt|ig|x+|_+|s|[0-9]+)$/;

/** Peel filler until stable: `xXtheRealAdmin123Xx` -> `admin`. */
function stripFiller(skel: string): string {
  let s = skel;
  for (let i = 0; i < 8; i++) {
    const next = s.replace(FILLER_PREFIX, "").replace(FILLER_SUFFIX, "");
    if (next === s || next.length < 2) break;
    s = next;
  }
  return s || skel;
}

/** Split a handle the way a reader does: on separators, digit runs and camelCase. */
export function tokenize(raw: string): string[] {
  const parts = raw
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Za-z])([0-9])/g, "$1 $2")
    .replace(/([0-9])([A-Za-z])/g, "$1 $2")
    .split(/[^A-Za-z0-9]+/)
    .filter((p) => p && !/^[0-9]+$/.test(p)); // digit runs are separators, not words
  return [...new Set(parts.map(skeletonize).filter(Boolean))];
}

/* --------------------------------------------------------------- matching -- */

/**
 * Compile a term into a pattern whose letter runs may only grow. A run of n
 * identical characters matches n or more, so `fuck` catches `fuuuck` while
 * `coon` still refuses to match `con`.
 */
function expandingPattern(term: string): string {
  const skel = skeletonize(term);
  let out = "";
  for (let i = 0; i < skel.length; ) {
    let n = 1;
    while (skel[i + n] === skel[i]) n++;
    out += `${escapeRe(skel[i])}{${n},}`;
    i += n;
  }
  return out;
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

interface CompiledList {
  /** Anchored: whole skeleton is the term. */
  exact: RegExp;
  /** Unanchored: term appears anywhere. Only terms longer than the list's `shortMax`. */
  anywhere: RegExp;
  /** Terms short enough that substring matching is unsafe; matched per token instead. */
  shortExact: RegExp;
  terms: string[];
}

/*
 * Where substring matching stops being safe. Slurs and profanity are matched
 * inside words from 4 characters up (`sh1tlord`, `coonhunter`) because the
 * innocent-word mask covers the known collisions. Brands and role words use a
 * higher cut: `meta`, `nike` and `mod` are far too common inside ordinary
 * handles, so at that length they must stand as their own token.
 */
const STEM_MIN_SUBSTRING = 3; // lists where stems of 4+ may match inside a word
const WORD_MIN_SUBSTRING = 4; // lists where only 5+ may match inside a word

/** Folded term -> the word as written, so logs read `tesla`, not `tesia`. */
const LABELS = new Map<string, string>();

function label(folded: string): string {
  return LABELS.get(folded) ?? folded;
}

function compile(terms: readonly string[], shortMax = WORD_MIN_SUBSTRING): CompiledList {
  for (const t of terms) {
    const k = skeletonize(t);
    if (k && !LABELS.has(k)) LABELS.set(k, t);
  }
  const skels = [...new Set(terms.map(skeletonize).filter(Boolean))];
  const long = skels.filter((t) => t.length > shortMax);
  const short = skels.filter((t) => t.length <= shortMax);
  const alt = (list: string[]) => (list.length ? list.map(expandingPattern).join("|") : "(?!)");
  return {
    exact: new RegExp(`^(?:${alt(skels)})$`),
    anywhere: new RegExp(`(?:${alt(long)})`),
    shortExact: new RegExp(`^(?:${alt(short)})$`),
    terms: skels,
  };
}

/** Which term in a list produced the hit - for the moderation log. */
function hit(list: CompiledList, value: string, how: "exact" | "anywhere"): string | null {
  for (const t of list.terms) {
    const p = expandingPattern(t);
    if (new RegExp(how === "exact" ? `^(?:${p})$` : `(?:${p})`).test(value)) return t;
  }
  return null;
}

/** Damerau-Levenshtein with an early exit once the distance exceeds `max`. */
export function editDistance(a: string, b: string, max = 3): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > max) return max + 1;
  const prev2: number[] = [];
  let prev: number[] = [];
  let row: number[] = [];
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    row = [i];
    let best = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(row[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, prev2[j - 2] + 1);
      row[j] = v;
      best = Math.min(best, v);
    }
    if (best > max) return max + 1;
    prev2.length = 0;
    prev2.push(...prev);
    prev = row;
  }
  return prev[b.length];
}

/**
 * How far a handle may sit from a protected term before it counts as an
 * impersonation attempt. Short terms get no slack: `mod` must not swallow `mop`.
 */
function fuzzyBudget(term: string): number {
  if (term.length >= 9) return 2;
  if (term.length >= 6) return 1;
  return 0;
}

/**
 * Only candidates no longer than the term are compared. A squat drops or swaps
 * characters (`suport`, `gogle`, `atriumsupprt`); a handle that merely *extends*
 * a protected word is usually honest (`stripey`, `appleton`, `amazing`), and the
 * cases that are not - `paypall`, `nikes` - are caught by run-expansion and
 * filler stripping instead.
 */
function nearest(skel: string, terms: readonly string[]): string | null {
  for (const t of terms) {
    const budget = fuzzyBudget(t);
    // Equal-length swaps that survive folding are honest words (`googly`, `verifier`);
    // the squats that matter drop characters.
    if (!budget || skel.length >= t.length || t.length - skel.length > budget) continue;
    if (editDistance(skel, t, budget) <= budget) return t;
  }
  return null;
}

/* ------------------------------------------------------------ compilation -- */

/** `atrium` plus the staff handles people would build from it: `atriumsupport`, `atriumhq`. */
const BRAND_STAFF = QUALIFIER_WORDS.map((q) => `${BRAND}${q}`).concat(QUALIFIER_WORDS.map((q) => `${q}${BRAND}`));

const RESERVED = compile([...RESERVED_ROUTES, BRAND]);
const AUTHORITY = compile([...AUTHORITY_TERMS, ...BRAND_STAFF]);
const ROLE_HANDLE = compile(ROLE_HANDLE_TERMS);
const TEST = compile(TEST_TERMS);
const COMPANIES = compile(COMPANY_NAMES);
const HATE = compile(HATE_TERMS, STEM_MIN_SUBSTRING);
const PROFANITY = compile(PROFANITY_STEMS, STEM_MIN_SUBSTRING);
const SLURS = compile(SLUR_STEMS, STEM_MIN_SUBSTRING);

/**
 * Protected name wrapped in nothing but qualifiers and digits: `free_netflix`,
 * `paypalrefunds`, `the-real-atrium-hq`. The repetition is bounded so the
 * pattern cannot backtrack catastrophically.
 */
const QUALIFIER_ALT = QUALIFIER_WORDS.map(expandingPattern).join("|");
function qualifierWrapped(terms: readonly string[]): RegExp {
  const pad = `(?:${QUALIFIER_ALT}){0,3}`;
  return new RegExp(`^${pad}(?:${terms.map(expandingPattern).join("|")})${pad}$`);
}
const BRAND_SCAM = qualifierWrapped(COMPANY_NAMES);
const AUTHORITY_SCAM = qualifierWrapped(AUTHORITY_TERMS.filter((t) => t.length >= 3));
/*
 * Only our own name gets the padded treatment. Route words are too ordinary:
 * `my_blog`, `the_store` and `news_updates` are honest handles, and a route
 * only actually clashes when it *is* the whole handle.
 */
const RESERVED_SCAM = qualifierWrapped([BRAND]);

/*
 * Fuzzy matching is reserved for names worth squatting: companies and the
 * handful of role words that carry real authority. Every role word would be
 * too many - `verified` at distance 1 catches `verifier`, an honest handle.
 */
const FUZZY_TARGETS = [
  ...new Set([
    ...COMPANIES.terms,
    ...["admin", "admins", "administrator", "moderator", "support", "helpdesk", "official", "sysadmin"].map(skeletonize),
    ...BRAND_STAFF.map(skeletonize),
    skeletonize(BRAND),
  ]),
];

const byLengthDesc = (a: string, b: string) => b.length - a.length;
const INNOCENT_FOLDED = INNOCENT_WORDS.map(skeletonize).filter(Boolean).sort(byLengthDesc);

/** Mask innocent words so the stems hiding inside them are never seen (the Scunthorpe guard). */
function maskInnocent(skel: string, extra: readonly string[] = []): string {
  const words = extra.length
    ? [...INNOCENT_FOLDED, ...extra.map(skeletonize).filter(Boolean)].sort(byLengthDesc)
    : INNOCENT_FOLDED;
  let out = skel;
  for (const w of words) out = out.split(w).join("~");
  return out;
}

/* ---------------------------------------------------------------- verdict -- */

const MESSAGES: Record<UsernameCategory, string> = {
  shape: "Usernames are 2-24 letters, numbers or underscores.",
  reserved: "That username is reserved.",
  authority: "That username suggests an official or staff account.",
  test: "Please choose a real username rather than a placeholder.",
  brand: "That username matches a company or product name.",
  slur: "That username contains language we don't allow.",
  hate: "That username contains language we don't allow.",
  profanity: "That username contains language we don't allow.",
  impersonation: "That username is too close to a protected or official name.",
};

function reject(category: UsernameCategory, term: string, skeleton: string, action: UsernameAction = "block"): UsernameVerdict {
  return { ok: false, action, category, term: label(term), message: MESSAGES[category], skeleton };
}

/**
 * Check a candidate handle against the policy. Pure and synchronous: no
 * uniqueness check, so run it alongside the "already taken" query.
 */
export function checkUsername(raw: string, opts: UsernamePolicyOptions = {}): UsernameVerdict {
  const value = (raw ?? "").trim();
  const skel = skeletonize(value);

  // 1. Shape.
  if (value.length < USERNAME_MIN || value.length > USERNAME_MAX) return reject("shape", "length", skel);
  if (!USERNAME_CHARSET.test(value)) return reject("shape", "charset", skel);
  if (/^_|_$/.test(value)) return reject("shape", "edge-underscore", skel);
  if (/__/.test(value)) return reject("shape", "double-underscore", skel);
  if (!/[a-zA-Z]/.test(value)) return reject("shape", "no-letter", skel); // all-digit handles collide with public ids
  if (!skel) return reject("shape", "empty-skeleton", skel);

  const stripped = stripFiller(skel);
  const tokens = [...new Set([...tokenize(value), skel, stripped])];
  const masked = maskInnocent(skel, opts.extraInnocent);
  const maskedTokens = tokens.map((t) => maskInnocent(t, opts.extraInnocent));

  // 2/3. Whole-handle matches, before and after peeling filler.
  for (const form of [skel, stripped]) {
    if (HATE.exact.test(form)) return reject("hate", hit(HATE, form, "exact") ?? form, skel);
    if (SLURS.exact.test(form)) return reject("slur", hit(SLURS, form, "exact") ?? form, skel);
    if (AUTHORITY.exact.test(form)) return reject("authority", hit(AUTHORITY, form, "exact") ?? form, skel);
    if (ROLE_HANDLE.exact.test(form)) return reject("authority", hit(ROLE_HANDLE, form, "exact") ?? form, skel);
    if (TEST.exact.test(form)) return reject("test", hit(TEST, form, "exact") ?? form, skel);
    if (COMPANIES.exact.test(form))
      return reject("brand", hit(COMPANIES, form, "exact") ?? form, skel, opts.brandAction ?? "block");
  }

  // Routes clash only when the handle *is* the route, so no filler stripping here:
  // `the_store` is a shop owner, `store` is a URL collision.
  if (RESERVED.exact.test(skel)) return reject("reserved", hit(RESERVED, skel, "exact") ?? skel, skel);

  // A protected name padded out with qualifiers only: the_real_admin, free_netflix.
  if (AUTHORITY_SCAM.test(skel)) return reject("authority", hit(AUTHORITY, skel, "anywhere") ?? skel, skel);
  if (BRAND_SCAM.test(skel))
    return reject("brand", hit(COMPANIES, skel, "anywhere") ?? skel, skel, opts.brandAction ?? "block");
  if (RESERVED_SCAM.test(skel)) return reject("reserved", hit(RESERVED, skel, "anywhere") ?? skel, skel);

  // 4. Slurs, hate and profanity anywhere in the handle.
  const extraSlurs = opts.extraSlurs?.length ? compile(opts.extraSlurs) : null;
  for (const [cat, list] of [
    ["hate", HATE],
    ["slur", SLURS],
    ...(extraSlurs ? ([["slur", extraSlurs]] as const) : []),
    ...(opts.allowProfanity ? [] : ([["profanity", PROFANITY]] as const)),
  ] as [UsernameCategory, CompiledList][]) {
    if (list.anywhere.test(masked)) return reject(cat, hit(list, masked, "anywhere") ?? "", skel);
    for (const t of maskedTokens) if (list.shortExact.test(t)) return reject(cat, hit(list, t, "exact") ?? t, skel);
  }

  // 5. A role word standing on its own: atrium_support_team, admin.of.atrium.
  for (const t of tokens) {
    if (AUTHORITY.exact.test(t)) return reject("authority", hit(AUTHORITY, t, "exact") ?? t, skel);
    // Short placeholders (`qa`, `tmp`) only matter as the whole handle: `qa_chen` is a person.
    if (t.length >= 4 && TEST.exact.test(t)) return reject("test", hit(TEST, t, "exact") ?? t, skel);
    // A brand as one token among others (`tesla_fan`, `disney_mom`) is usually a
    // fan account, not a fake: flag for review rather than refuse outright.
    if (COMPANIES.exact.test(t)) return reject("brand", hit(COMPANIES, t, "exact") ?? t, skel, opts.brandAction ?? "review");
  }

  // 6. Near-misses of protected names.
  const extraReserved = opts.extraReserved?.length ? compile(opts.extraReserved) : null;
  if (extraReserved && (extraReserved.exact.test(skel) || extraReserved.exact.test(stripped)))
    return reject("reserved", hit(extraReserved, skel, "exact") ?? skel, skel);
  const near = nearest(skel, FUZZY_TARGETS) ?? nearest(stripped, FUZZY_TARGETS);
  if (near) return reject("impersonation", near, skel);

  return { ok: true, action: "allow", skeleton: skel };
}

/** Convenience wrapper for call sites that only need a yes/no. */
export function isUsernameAllowed(raw: string, opts?: UsernamePolicyOptions): boolean {
  return checkUsername(raw, opts).action === "allow";
}
